import React, { useEffect, useState } from 'react';
import { History } from 'lucide-react';
import AccordionSection from '../UI/AccordionSection';
import { CountBadge } from '../UI/CountBadge';
import { TradeBadge } from '../UI/TradeBadge';
import { StatusBadge } from '../UI/StatusBadge';
import LoadingSpinner from '../UI/LoadingSpinner';
import { staffAPI } from '../../services/api';
import { formatDate } from '../../utils/dateUtils';
import { buildMovementDiffs } from '../../utils/employmentMovementDiffs';
import MovementDiffLines from './MovementDiffLines';

const CHANGE_TYPE_COLORS = {
  initial: 'bg-gray-100 text-gray-700',
  promotion: 'bg-emerald-100 text-emerald-700',
  transfer: 'bg-blue-100 text-blue-700',
  redesignation: 'bg-purple-100 text-purple-700',
  trade_change: 'bg-orange-100 text-orange-700',
  lateral_move: 'bg-cyan-100 text-cyan-700',
  correction: 'bg-yellow-100 text-yellow-700',
  ro_change: 'bg-indigo-100 text-indigo-700',
  type_change: 'bg-teal-100 text-teal-700',
  tracking_change: 'bg-amber-100 text-amber-700',
  status_change: 'bg-rose-100 text-rose-700',
  salary_change: 'bg-amber-100 text-amber-700',
};

const MovementChangeDetails = ({ movement, includeSalary }) => {
  const diffs = buildMovementDiffs(movement, { includeSalary });

  if (movement.change_type === 'initial') {
    return (
      <div className="space-y-0.5 text-sm">
        {movement.to_department_display && (
          <div><span className="text-gray-500 text-xs">Dept:</span> {movement.to_department_display}</div>
        )}
        {movement.to_designation && (
          <div><span className="text-gray-500 text-xs">Designation:</span> {movement.to_designation}</div>
        )}
        {movement.to_trade_category_display && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-gray-500 text-xs">Trade:</span>
            <TradeBadge name={movement.to_trade_category_display} size="xs" />
          </div>
        )}
      </div>
    );
  }

  if (diffs.length > 0) {
    return (
      <div className="text-sm">
        <MovementDiffLines diffs={diffs} />
      </div>
    );
  }

  return <span className="text-sm text-gray-400">No visible changes</span>;
};

/**
 * Collapsible employment movement history (promotions, transfers, etc.).
 * Pass `movements` + `loading` when the parent already fetches them (e.g. admin tab).
 */
export default function EmploymentHistorySection({
  staffId,
  theme = 'admin',
  detailLevel = 'full',
  /** 'accordion' = collapsible card; 'flat' = table only (for PillToggle host) */
  variant = 'accordion',
  movements: externalMovements,
  loading: externalLoading,
}) {
  const [expanded, setExpanded] = useState(false);
  const [internalMovements, setInternalMovements] = useState([]);
  const [internalLoading, setInternalLoading] = useState(false);

  const isControlled = externalMovements !== undefined;
  const movements = isControlled ? externalMovements : internalMovements;
  const loading = isControlled ? (externalLoading ?? false) : internalLoading;
  const showFullDetail = detailLevel === 'full';
  const isFlat = variant === 'flat';
  const iconColor = theme === 'employee' ? 'text-blue-800' : 'text-red-600';
  const iconBg = theme === 'employee' ? 'bg-blue-100' : 'bg-red-50';

  useEffect(() => {
    if (isControlled || !staffId) return;

    setInternalLoading(true);
    staffAPI.getEmploymentMovements(staffId)
      .then((res) => setInternalMovements(res.data || []))
      .catch(() => setInternalMovements([]))
      .finally(() => setInternalLoading(false));
  }, [staffId, isControlled]);

  if (!staffId) return null;

  const body = (
    <>
      {!isFlat && (
        <div className="border-t border-gray-200 px-4 py-3 bg-gray-50/60">
          <p className="text-sm text-gray-600 max-w-2xl">
            Org events recorded via Record Employment Event (hire baseline, promotions, transfers, RO / status / salary changes).
            {showFullDetail ? (
              <>
                {' '}Join date and probation milestones are on the Timeline view of{' '}
                <span className="font-medium text-gray-700">Career Timeline & History</span>.
              </>
            ) : null}
          </p>
        </div>
      )}
      {loading ? (
        <div className={`flex items-center justify-center gap-3 py-12 ${isFlat ? '' : 'border-t border-gray-200'}`}>
          <LoadingSpinner size="md" />
          <span className="text-sm text-gray-500">Loading history…</span>
        </div>
      ) : movements.length === 0 ? (
        <div className={`text-center py-12 ${isFlat ? '' : 'border-t border-gray-200'}`}>
          <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No employment history recorded</h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            Promotions, transfers, and other org events will appear here once recorded.
            {showFullDetail && !isFlat
              ? ' For join date and probation, switch Career Timeline & History to Timeline view.'
              : ''}
          </p>
        </div>
      ) : (
        <div className={`overflow-x-auto ${isFlat ? 'bg-white rounded-xl border border-gray-200' : 'border-t border-gray-200'}`}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="table-header">
              <tr>
                <th className={`table-header-cell ${showFullDetail ? 'text-right' : ''}`}>Effective</th>
                <th className="table-header-cell">Type</th>
                <th className="table-header-cell">Change Details</th>
                {showFullDetail && (
                  <>
                    <th className="table-header-cell">Reason</th>
                    <th className="table-header-cell">By</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="table-body">
              {movements.map((movement) => (
                <tr key={movement.id} className="table-row">
                  <td className={`table-cell whitespace-nowrap ${showFullDetail ? 'text-right' : ''}`}>
                    {formatDate(movement.effective_from)}
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${CHANGE_TYPE_COLORS[movement.change_type] || 'bg-gray-100 text-gray-700'}`}>
                        {movement.change_type_display}
                      </span>
                      {movement.is_scheduled && (
                        <StatusBadge status="upcoming" label="Scheduled" />
                      )}
                    </div>
                  </td>
                  <td className="table-cell">
                    <MovementChangeDetails movement={movement} includeSalary={showFullDetail} />
                  </td>
                  {showFullDetail && (
                    <>
                      <td className="table-cell max-w-[200px] truncate text-sm text-gray-500">
                        {movement.change_reason || '—'}
                      </td>
                      <td className="table-cell align-top min-w-0">
                        <div className="min-w-0">
                          <div className="text-sm text-gray-900 truncate" title={movement.created_by_staff_id || undefined}>
                            {movement.created_by_name || '—'}
                          </div>
                          {movement.created_by_staff_id
                            && movement.created_by_name
                            && movement.created_by_name !== movement.created_by_staff_id
                            && movement.created_by_name !== 'System' && (
                            <div className="text-[11px] font-mono text-gray-400 truncate">
                              {movement.created_by_staff_id}
                            </div>
                          )}
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );

  if (isFlat) {
    return body;
  }

  return (
    <AccordionSection
      expanded={expanded}
      onToggle={() => setExpanded((open) => !open)}
      theme={theme}
      contentVariant="flush"
      icon={<History className={`w-4 h-4 ${iconColor}`} />}
      iconBg={iconBg}
      title="Employment History"
      trailing={movements.length > 0 ? (
        <CountBadge count={movements.length} theme={theme} />
      ) : null}
    >
      {body}
    </AccordionSection>
  );
}
